import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import EmptyState from "@/components/feature/EmptyState";
import RelatedVideos from "@/pages/detail/components/RelatedVideos";
import { usePlayNotice } from "@/components/feature/PlayNoticeProvider";
import { videos } from "@/mocks/videos";
import { formatCount } from "@/utils/format";
import { usePageMeta } from "@/hooks/usePageMeta";

export default function DetailPage() {
  const { videoId } = useParams();
  const { showNotice } = usePlayNotice();

  const video = useMemo(() => videos.find((item) => item.id === videoId), [videoId]);

  usePageMeta(
    video ? `${video.title} - 樱桃视频影视详情` : "视频未找到 - 樱桃视频",
    video
      ? `${video.description.slice(0, 60)} 分类：${video.categoryName} · ${video.subCategory}`
      : "该视频不存在或已被删除"
  );

  if (!video) {
    return (
      <div className="w-full px-4 md:px-8 py-16">
        <EmptyState
          message="视频不存在或已被删除"
          hint="该视频可能已被移除，去首页看看其他精彩内容吧。"
          actionHref="/"
          actionText="回到首页"
        />
      </div>
    );
  }

  const metaItems = [
    { icon: "ri-user-3-line", text: video.author },
    { icon: "ri-calendar-line", text: video.uploadDate },
    { icon: "ri-time-line", text: video.duration },
    { icon: "ri-earth-line", text: video.region },
  ];

  return (
    <div className="w-full px-4 md:px-8 py-8 md:py-10">
      <Breadcrumb
        items={[
          { label: "首页", to: "/" },
          { label: video.categoryName, to: `/category/${video.categorySlug}` },
          { label: video.title },
        ]}
      />

      <div className="mt-6 max-w-4xl">
        {/* cover */}
        <div className="relative w-full aspect-video overflow-hidden rounded-lg bg-background-200">
          <img
            src={video.cover}
            alt={video.title}
            title={`${video.title} - 樱桃视频`}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/70 via-foreground-950/10 to-transparent"></div>
          <button
            type="button"
            onClick={() => showNotice(video.title)}
            className="absolute inset-0 flex flex-col items-center justify-center gap-3 cursor-pointer group"
          >
            <span className="w-16 h-16 md:w-20 md:h-20 flex items-center justify-center rounded-full bg-primary-500 text-background-50 transition-transform group-hover:scale-110">
              <i className="ri-play-fill text-4xl"></i>
            </span>
            <span className="rounded-full bg-foreground-950/60 px-3 py-1 text-xs text-background-50">
              播放功能待上线
            </span>
          </button>
          <span className="absolute top-3 left-3 rounded-full bg-primary-500 px-3 py-1 text-xs font-label text-background-50">
            {video.categoryName} · {video.subCategory}
          </span>
          <span className="absolute bottom-3 right-3 rounded-md bg-foreground-950/70 px-2.5 py-1 text-xs font-label text-background-50">
            {video.duration}
          </span>
        </div>

        {/* info */}
        <div className="mt-6">
          <h1 className="text-2xl md:text-3xl font-heading font-bold leading-snug text-foreground-950">
            {video.title}
          </h1>

          <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-foreground-600">
            {metaItems.map((item) => (
              <span key={item.icon} className="flex items-center gap-1.5">
                <i className={`${item.icon} text-foreground-400`}></i>
                {item.text}
              </span>
            ))}
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <span className="flex items-center gap-1 rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-900">
              <i className="ri-thumb-up-line"></i>
              {formatCount(video.likes)} 点赞
            </span>
            <span className="flex items-center gap-1 rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-900">
              <i className="ri-chat-3-line"></i>
              {formatCount(video.comments)} 评论
            </span>
            <span className="flex items-center gap-1 rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-900">
              <i className="ri-eye-line"></i>
              {formatCount(video.views)} 播放
            </span>
          </div>

          <div className="mt-6 rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
            <h2 className="text-base font-heading font-semibold text-foreground-950">视频简介</h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-700">{video.description}</p>
            <div className="mt-4 flex flex-wrap gap-2">
              {video.tags.map((tag) => (
                <span
                  key={tag}
                  className="rounded-full bg-background-100 px-3 py-1 text-xs text-foreground-600"
                >
                  #{tag}
                </span>
              ))}
            </div>
          </div>

          <Link
            to={`/category/${video.categorySlug}`}
            className="mt-6 inline-flex items-center gap-2 rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-label text-foreground-700 hover:border-primary-300 hover:text-primary-600 transition-colors whitespace-nowrap"
          >
            <i className="ri-arrow-left-line"></i>
            返回{video.categoryName}分类
          </Link>
        </div>
      </div>

      <div className="mt-12 md:mt-16">
        <RelatedVideos video={video} />
      </div>
    </div>
  );
}